// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import Keybind from '@app/features/input/state/InputKeybind';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import {CameraPermissionDeniedModal} from '@app/features/voice/components/alerts/CameraPermissionDeniedModal';
import {MicrophonePermissionDeniedModal} from '@app/features/voice/components/alerts/MicrophonePermissionDeniedModal';
import {
	applyAllLocalAudioPreferences as applyAllLocalAudioPreferencesImpl,
	applyLocalAudioPreferencesForUser as applyLocalAudioPreferencesForUserImpl,
	applyPushToMuteHold as applyPushToMuteHoldImpl,
	applyPushToTalkHold as applyPushToTalkHoldImpl,
	getEffectiveSelfMuteForVoiceStatePayload,
	getMuteReason as getMuteReasonImpl,
	handlePushToTalkModeChange as handlePushToTalkModeChangeImpl,
	isPushToMuteActive,
	isPushToTalkActive,
	type VoiceMuteReason,
} from '@app/features/voice/engine/VoiceAudioManager';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import VoiceDevicePermissionState from '@app/features/voice/engine/VoiceDevicePermissionState';
import type {EffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {playEntranceSound} from '@app/features/voice/engine/VoiceEntranceSoundManager';
import {resolveLocalSpeakingOverrideState} from '@app/features/voice/engine/VoiceLocalSpeakingGate';
import {
	getRoomFromMediaEngine,
	syncLocalVoiceStateWithServer,
	updateLocalParticipantFromRoom,
} from '@app/features/voice/engine/VoiceMediaEngineBridge';
import VoiceMediaStateCoordinator from '@app/features/voice/engine/VoiceMediaStateCoordinator';
import {
	createVoiceMediaSnapshot,
	transitionVoiceMediaSnapshot,
	type VoiceMediaEvent,
	type VoiceMediaSnapshot,
} from '@app/features/voice/engine/VoiceMediaStateMachine';
import VoicePermissionManager from '@app/features/voice/engine/VoicePermissionManager';
import VoiceScreenShareManager, {
	type DeviceScreenShareCaptureOptions,
	type ScreenShareReconnectSnapshot,
} from '@app/features/voice/engine/VoiceScreenShareManager';
import {
	getLocalSpeakingThresholdRms,
	SPEAKING_LOCAL_RELEASE_MS,
} from '@app/features/voice/engine/VoiceSpeakingThreshold';
import type {VoiceStateSyncPartial} from '@app/features/voice/engine/VoiceStateSyncStateMachine';
import {
	enforceLocalMediaPublicationCap,
	getLocalMicrophonePublications,
	getPrimaryLocalMicrophonePublication,
} from '@app/features/voice/engine/VoiceTrackPublicationUtils';
import type {NativeScreenShareOptions} from '@app/features/voice/engine/voice_screen_share_manager/DisplayMediaCapture';
import CallMediaPrefs from '@app/features/voice/state/CallMediaPrefs';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import MediaPermission from '@app/features/voice/state/MediaPermission';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {buildMicrophonePublishOptions} from '@app/features/voice/utils/AudioPublishOptions';
import {ensureNativePermission} from '@app/features/voice/utils/NativePermissions';
import {applyBackgroundProcessor} from '@app/features/voice/utils/VideoBackgroundProcessor';
import {
	removeVoiceInputProcessor,
	syncVoiceInputProcessor,
	updateVoiceInputGain,
} from '@app/features/voice/utils/VoiceInputProcessor';
import {isVoiceSpeakPermissionDenied} from '@app/features/voice/utils/VoicePermissionUtils';
import {
	applyContentHintToTrack,
	resolveVoiceProcessingFromStateForDeviceLabel,
} from '@app/features/voice/utils/VoiceProcessingProfile';
import type {NativeAudioStartOptions} from '@app/types/electron.d';
import type {
	AudioCaptureOptions,
	LocalAudioTrack,
	LocalTrackPublication,
	LocalVideoTrack,
	Room,
	ScreenShareCaptureOptions,
	TrackPublishOptions,
} from 'livekit-client';
import {Track, VideoPresets} from 'livekit-client';
import {makeAutoObservable} from 'mobx';

const logger = new Logger('VoiceMediaManager');
const LOCAL_SPEAKING_ANALYSER_INTERVAL_MS = 50;

export interface SetCameraEnabledOptions {
	deviceId?: string;
	sendUpdate?: boolean;
}

export interface RefreshMicrophoneOptions {
	forceRepublish?: boolean;
}

class VoiceMediaManager {
	private speakingAudioContext: AudioContext | null = null;
	private speakingSourceNode: MediaStreamAudioSourceNode | null = null;
	private speakingAnalyserNode: AnalyserNode | null = null;
	private speakingTimerId: number | null = null;
	private speakingSilenceStartedAt: number | null = null;
	private speakingTrackEndedCleanup: (() => void) | null = null;
	private mediaStateSnapshot: VoiceMediaSnapshot = createVoiceMediaSnapshot();
	private microphoneEnablePromise: Promise<void> | null = null;
	private microphoneRefreshQueue: Promise<void> = Promise.resolve();
	private cameraBackgroundRefreshQueue: Promise<void> = Promise.resolve();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	private isSpeakPermissionDenied(channelId: string | null): boolean {
		return isVoiceSpeakPermissionDenied(VoiceConnectionManager.guildId, channelId);
	}

	private transitionMediaState(event: VoiceMediaEvent): void {
		const nextSnapshot = transitionVoiceMediaSnapshot(this.mediaStateSnapshot, event);
		this.mediaStateSnapshot =
			nextSnapshot.context.commands.length > 0
				? transitionVoiceMediaSnapshot(nextSnapshot, {type: 'commands.clear'})
				: nextSnapshot;
	}

	private updateMediaAudioControls(): void {
		this.transitionMediaState({
			type: 'audio.controls.update',
			controls: {
				pushToTalkActive: isPushToTalkActive(),
				pushToTalkHeld: Keybind.pushToTalkHeld,
				pushToMuteActive: isPushToMuteActive(),
				pushToMuteHeld: Keybind.pushToMuteHeld,
			},
		});
	}

	private async enforceSpeakPermissionMute(room: Room): Promise<void> {
		await this.disableMicrophone(room);
		this.syncVoiceState({self_mute: true});
		this.syncLocalSpeakingOverride(room);
	}

	get isScreenSharePending(): boolean {
		return VoiceScreenShareManager.getIsScreenSharePending();
	}

	async prepareMicrophonePermissionForConnect(): Promise<boolean> {
		this.transitionMediaState({type: 'permission.warmup.start'});
		const devicePermission = VoiceDevicePermissionState.getState().permissionStatus;
		if (MediaPermission.isMicrophoneGranted() || devicePermission === 'granted') {
			this.transitionMediaState({type: 'permission.warmup.granted'});
			return true;
		}
		if (MediaPermission.isMicrophoneExplicitlyDenied() || devicePermission === 'denied') {
			this.transitionMediaState({type: 'permission.warmup.denied'});
			this.handleMicrophonePermissionDenied();
			return false;
		}
		if (isDesktop()) {
			try {
				const nativeResult = await ensureNativePermission('microphone');
				if (nativeResult === 'denied') {
					logger.warn('Native microphone permission denied before voice connect');
					this.transitionMediaState({type: 'permission.warmup.denied'});
					this.handleMicrophonePermissionDenied();
					return false;
				}
				if (nativeResult === 'granted') {
					MediaPermission.updateMicrophonePermissionGranted();
					this.transitionMediaState({type: 'permission.warmup.granted'});
					return true;
				}
			} catch (error) {
				logger.warn('Native microphone permission check failed before voice connect; continuing', {error});
			}
		}
		if (!navigator.mediaDevices?.getUserMedia) {
			this.transitionMediaState({type: 'permission.warmup.unavailable'});
			return true;
		}
		try {
			const stream = await navigator.mediaDevices.getUserMedia({audio: true});
			stream.getTracks().forEach((track) => track.stop());
			MediaPermission.updateMicrophonePermissionGranted();
			this.transitionMediaState({type: 'permission.warmup.granted'});
			return true;
		} catch (error) {
			if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
				logger.warn('Microphone permission denied before voice connect', {error});
				this.transitionMediaState({type: 'permission.warmup.denied'});
				this.handleMicrophonePermissionDenied();
				return false;
			}
			logger.warn('Microphone permission warm-up failed before voice connect; continuing', {error});
			this.transitionMediaState({type: 'permission.warmup.failedContinuing'});
			return true;
		}
	}

	async ensureMicrophone(room: Room, channelId: string): Promise<void> {
		if (this.isSpeakPermissionDenied(channelId)) {
			logger.debug('Skipping microphone: speak permission denied');
			await this.enforceSpeakPermissionMute(room);
			return;
		}
		const audioState = getEffectiveAudioState();
		const denied = MediaPermission.isMicrophoneExplicitlyDenied();
		const isPttMutedOnly =
			isPushToTalkActive() &&
			!Keybind.pushToTalkHeld &&
			!audioState.selfMute &&
			!audioState.serverMute &&
			!audioState.effectiveDeaf;
		if (audioState.effectiveMute && !isPttMutedOnly) {
			logger.debug('Skipping: effective audio state is muted or deafened', audioState);
			if (audioState.selfMute) {
				this.syncVoiceState({self_mute: true});
			}
			return;
		}
		if (denied) {
			logger.debug('Microphone explicitly denied');
			if (!LocalVoiceState.getSelfMute()) {
				LocalVoiceState.updateSelfMute(true);
			}
			this.syncVoiceState({self_mute: true});
			return;
		}
		if (!room.localParticipant) {
			logger.warn('No local participant');
			return;
		}
		try {
			await this.enableMicrophone(room, channelId);
			MediaPermission.updateMicrophonePermissionGranted();
			if (isPttMutedOnly) {
				getLocalMicrophonePublications(room.localParticipant).forEach((publication) => {
					publication.mute().catch((error) => logger.error('Failed to mute publication for PTT', {error}));
				});
				this.syncVoiceState({self_mute: true});
			} else {
				this.syncVoiceState({self_mute: audioState.selfMute});
			}
			this.syncLocalSpeakingOverride(room);
		} catch (e: unknown) {
			if (e instanceof Error && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')) {
				this.handleMicrophonePermissionDenied();
				this.syncVoiceState({self_mute: true});
			}
		}
	}

	async reconcileEffectiveAudioState(
		room: Room | null,
		params: {
			channelId: string;
			serverMute: boolean;
			serverDeaf: boolean;
		},
	): Promise<void> {
		if (!room?.localParticipant) {
			logger.debug('Skipping audio-state reconciliation: no local participant');
			return;
		}
		const permissionMuted = this.isSpeakPermissionDenied(params.channelId);
		const audioState = this.getEffectiveAudioState({
			serverMute: params.serverMute || permissionMuted,
			serverDeaf: params.serverDeaf,
		});
		logger.info('Reconciling local media after voice state update', {
			channelId: params.channelId,
			...audioState,
		});
		this.transitionMediaState({
			type: 'audio.reconcile',
			audioState,
			controls: {
				pushToTalkActive: isPushToTalkActive(),
				pushToTalkHeld: Keybind.pushToTalkHeld,
				pushToMuteActive: isPushToMuteActive(),
				pushToMuteHeld: Keybind.pushToMuteHeld,
			},
			permissionMuted,
			hasLiveMicrophonePublication: this.hasLiveMicrophonePublication(room),
		});
		VoicePermissionManager.applyDeafen(room, audioState.effectiveDeaf);
		this.applyAllLocalAudioPreferences(room);
		if (permissionMuted) {
			await this.enforceSpeakPermissionMute(room);
			return;
		}
		const pttActive = isPushToTalkActive();
		const ptmActive = isPushToMuteActive();
		const effectiveMute =
			audioState.effectiveDeaf ||
			audioState.serverMute ||
			audioState.selfMute ||
			(pttActive && !Keybind.pushToTalkHeld) ||
			(ptmActive && Keybind.pushToMuteHeld);
		if (effectiveMute) {
			this.setAudioPublicationsMuted(room, true, 'voice state update');
			this.syncLocalSpeakingOverride(room);
			return;
		}
		if (!this.hasLiveMicrophonePublication(room)) {
			try {
				await this.enableMicrophone(room, params.channelId);
			} catch (error) {
				logger.warn('Failed to restore microphone while reconciling audio state', {
					error,
					channelId: params.channelId,
					audioState,
				});
				if (!LocalVoiceState.getSelfMute()) {
					LocalVoiceState.updateSelfMute(true);
				}
				this.syncVoiceState({self_mute: true});
				return;
			}
		}
		this.setAudioPublicationsMuted(room, false, 'voice state update');
		this.syncLocalSpeakingOverride(room);
	}

	private hasMicrophonePublication(room: Room): boolean {
		return this.getMicrophonePublication(room) !== null;
	}

	private hasLiveMicrophonePublication(room: Room): boolean {
		const publication = this.getMicrophonePublication(room);
		if (!publication) return false;
		const track = publication.track;
		if (!track) return false;
		const mediaStreamTrack = track.mediaStreamTrack;
		if (!mediaStreamTrack) return false;
		return mediaStreamTrack.readyState !== 'ended';
	}

	private getMicrophonePublication(room: Room): LocalTrackPublication | null {
		if (!room.localParticipant) return null;
		return getPrimaryLocalMicrophonePublication(room.localParticipant);
	}

	private resolveInputDeviceId(): string {
		const inputDeviceId = VoiceSettings.getInputDeviceId();
		const {inputDevices} = VoiceDevicePermissionState.getState();
		if (inputDevices.length === 0) return inputDeviceId;
		if (inputDevices.some((device) => device.deviceId === inputDeviceId)) {
			return inputDeviceId;
		}
		return inputDevices[0].deviceId;
	}

	private resolveActiveInputDeviceLabel(): string | null {
		const deviceId = this.resolveInputDeviceId();
		const {inputDevices} = VoiceDevicePermissionState.getState();
		const device = inputDevices.find((d) => d.deviceId === deviceId);
		return device?.label || null;
	}

	private getMicrophoneCaptureOptions(): AudioCaptureOptions {
		const profile = resolveVoiceProcessingFromStateForDeviceLabel(VoiceSettings, this.resolveActiveInputDeviceLabel());
		return {
			deviceId: this.resolveInputDeviceId(),
			echoCancellation: profile.echoCancellation,
			noiseSuppression: profile.browserNoiseSuppression,
			autoGainControl: profile.autoGainControl,
			voiceIsolation: false,
		};
	}

	private getMicrophonePublishOptions(channelId: string | null): TrackPublishOptions | undefined {
		const channelBitrate = channelId ? Channels.getChannel(channelId)?.bitrate : null;
		const profile = resolveVoiceProcessingFromStateForDeviceLabel(VoiceSettings, this.resolveActiveInputDeviceLabel());
		return buildMicrophonePublishOptions(channelBitrate, profile.mode);
	}

	async refreshMicrophonePublishSettings(room: Room | null, channelId: string | null): Promise<void> {
		if (!room?.localParticipant || !this.hasMicrophonePublication(room)) {
			return;
		}
		const audioTrack = this.getLocalAudioTrack(room);
		const sender = audioTrack?.sender;
		if (!sender) {
			logger.warn('No sender found for active microphone track');
			return;
		}
		const maxBitrate = this.getMicrophonePublishOptions(channelId)?.audioPreset?.maxBitrate;
		const senderParameters = sender.getParameters();
		const encodings = senderParameters.encodings?.length ? senderParameters.encodings : [{}];
		const isAlreadyApplied = encodings.every((encoding) => encoding.maxBitrate === maxBitrate);
		if (isAlreadyApplied) {
			return;
		}
		senderParameters.encodings = encodings.map((encoding) => {
			const nextEncoding = {...encoding};
			if (maxBitrate !== undefined) {
				nextEncoding.maxBitrate = maxBitrate;
			} else {
				delete nextEncoding.maxBitrate;
			}
			return nextEncoding;
		});
		try {
			await sender.setParameters(senderParameters);
			logger.debug('Updated microphone publish settings', {channelId, maxBitrate});
		} catch (error) {
			logger.warn('Failed to update microphone publish settings', {error, channelId, maxBitrate});
		}
	}

	async refreshMicrophone(room: Room | null, options: RefreshMicrophoneOptions = {}): Promise<void> {
		const refresh = async () => this.refreshMicrophoneNow(room, options);
		const pendingRefresh = this.microphoneRefreshQueue.then(refresh, refresh);
		this.microphoneRefreshQueue = pendingRefresh.catch(() => {});
		return pendingRefresh;
	}

	private async refreshMicrophoneNow(room: Room | null, options: RefreshMicrophoneOptions = {}): Promise<void> {
		if (!room?.localParticipant || !this.hasMicrophonePublication(room)) {
			return;
		}
		this.transitionMediaState({
			type: 'refresh.request',
			hasPublication: true,
			forceRepublish: options.forceRepublish,
		});
		const audioTrack = this.getLocalAudioTrack(room);
		if (!audioTrack) {
			return;
		}
		const captureOptions = this.getMicrophoneCaptureOptions();
		if (options.forceRepublish) {
			try {
				await this.republishMicrophone(room);
				this.transitionMediaState({type: 'refresh.republish.success'});
				logger.debug('Re-published microphone capture settings', {captureOptions});
			} catch (recoveryError) {
				this.transitionMediaState({type: 'refresh.republish.failure'});
				logger.error('Failed to re-publish microphone after capture settings change', recoveryError);
			}
			return;
		}
		const primaryMicPublication =
			Array.from(room.localParticipant.audioTrackPublications.values()).find((pub) => pub.track === audioTrack) ?? null;
		const shouldUnmuteAfter = primaryMicPublication ? !primaryMicPublication.isMuted : false;
		if (shouldUnmuteAfter) {
			await primaryMicPublication?.mute().catch((muteError) => {
				logger.warn('Failed to hold mic muted while refreshing voice input processor', {muteError});
			});
		}
		try {
			await audioTrack.restartTrack(captureOptions);
			await this.refreshMicrophonePublishSettings(room, VoiceConnectionManager.channelId);
			await syncVoiceInputProcessor(audioTrack);
			const profile = resolveVoiceProcessingFromStateForDeviceLabel(
				VoiceSettings,
				this.resolveActiveInputDeviceLabel(),
			);
			if (audioTrack.mediaStreamTrack) {
				applyContentHintToTrack(audioTrack.mediaStreamTrack, profile.contentHint);
			}
			this.startLocalSpeakingDetector(room, audioTrack.mediaStreamTrack ?? null);
			this.transitionMediaState({type: 'refresh.restart.success'});
			logger.debug('Refreshed microphone capture settings');
			if (shouldUnmuteAfter) {
				const selfMute = getEffectiveSelfMuteForVoiceStatePayload();
				const selfDeaf = LocalVoiceState.getSelfDeaf();
				if (!selfMute && !selfDeaf) {
					await primaryMicPublication?.unmute().catch((unmuteError) => {
						logger.warn('Failed to resume mic after refreshing voice input processor', {unmuteError});
					});
				}
			}
			return;
		} catch (error) {
			this.transitionMediaState({type: 'refresh.restart.failure'});
			logger.warn('Failed to restart microphone capture; re-publishing mic from scratch', {
				error,
				captureOptions,
			});
		}
		try {
			await this.republishMicrophone(room);
			this.transitionMediaState({type: 'refresh.republish.success'});
		} catch (recoveryError) {
			this.transitionMediaState({type: 'refresh.republish.failure'});
			logger.error('Failed to recover microphone after restart failure', recoveryError);
		}
	}

	private async republishMicrophone(room: Room): Promise<void> {
		await this.disableMicrophone(room);
		await new Promise((resolve) => setTimeout(resolve, 150));
		await this.enableMicrophone(room, VoiceConnectionManager.channelId);
	}

	async enableMicrophone(room: Room, channelId: string | null): Promise<void> {
		if (this.microphoneEnablePromise) {
			await this.microphoneEnablePromise;
			return;
		}
		this.microphoneEnablePromise = this.enableMicrophoneNow(room, channelId);
		try {
			await this.microphoneEnablePromise;
		} finally {
			this.microphoneEnablePromise = null;
		}
	}

	private async enableMicrophoneNow(room: Room, channelId: string | null): Promise<void> {
		this.transitionMediaState({
			type: 'microphone.enable.request',
			hasPublication: this.hasMicrophonePublication(room),
			hasLivePublication: this.hasLiveMicrophonePublication(room),
			speakPermissionDenied: this.isSpeakPermissionDenied(channelId),
			permissionDenied: MediaPermission.isMicrophoneExplicitlyDenied(),
		});
		if (this.isSpeakPermissionDenied(channelId)) {
			logger.debug('Skipping microphone enable: speak permission denied');
			await this.enforceSpeakPermissionMute(room);
			return;
		}
		if (this.hasMicrophonePublication(room)) {
			if (this.hasLiveMicrophonePublication(room)) {
				logger.debug('Microphone track already published, skipping duplicate publish');
				return;
			}
			logger.warn('Existing microphone publication has an ended track; unpublishing before reacquire');
			await this.disableMicrophone(room);
		}
		let microphoneWasPublished = false;
		try {
			if (isDesktop()) {
				const nativeResult = await ensureNativePermission('microphone');
				if (nativeResult === 'denied') {
					logger.warn('Native microphone permission denied');
					throw Object.assign(new Error('Native microphone permission denied'), {
						name: 'NotAllowedError',
					});
				}
				if (nativeResult === 'granted') {
					MediaPermission.updateMicrophonePermissionGranted();
				}
			}
			await VoiceDevicePermissionState.ensureDevices({requestPermissions: false});
			if (!room.localParticipant) {
				logger.warn('No local participant');
				this.transitionMediaState({type: 'microphone.enable.failure'});
				return;
			}
			await room.localParticipant.setMicrophoneEnabled(
				true,
				this.getMicrophoneCaptureOptions(),
				this.getMicrophonePublishOptions(channelId),
			);
			microphoneWasPublished = true;
			await this.refreshMicrophonePublishSettings(room, channelId);
			const audioTrack = this.getLocalAudioTrack(room);
			const primaryMicPublication = audioTrack
				? Array.from(room.localParticipant.audioTrackPublications.values()).find((pub) => pub.track === audioTrack)
				: null;
			const shouldUnmuteAfter = primaryMicPublication ? !primaryMicPublication.isMuted : false;
			if (primaryMicPublication && shouldUnmuteAfter) {
				await primaryMicPublication.mute().catch((error) => {
					logger.warn('Failed to hold mic muted while installing voice input processor', {error});
				});
			}
			await syncVoiceInputProcessor(audioTrack);
			if (primaryMicPublication && shouldUnmuteAfter) {
				const selfMute = getEffectiveSelfMuteForVoiceStatePayload();
				const selfDeaf = LocalVoiceState.getSelfDeaf();
				if (!selfMute && !selfDeaf) {
					await primaryMicPublication.unmute().catch((error) => {
						logger.warn('Failed to resume mic after installing voice input processor', {error});
					});
				}
			}
			const profile = resolveVoiceProcessingFromStateForDeviceLabel(
				VoiceSettings,
				this.resolveActiveInputDeviceLabel(),
			);
			if (audioTrack?.mediaStreamTrack) {
				applyContentHintToTrack(audioTrack.mediaStreamTrack, profile.contentHint);
			}
			this.startLocalSpeakingDetector(room, audioTrack?.mediaStreamTrack ?? null);
			MediaPermission.updateMicrophonePermissionGranted();
			this.transitionMediaState({type: 'microphone.enable.success'});
			logger.info('Successfully enabled microphone');
		} catch (e: unknown) {
			if (microphoneWasPublished) {
				await this.disableMicrophone(room);
			}
			if (e instanceof Error && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')) {
				logger.error('Permission denied');
				this.transitionMediaState({
					type: 'microphone.enable.failure',
					permissionDenied: true,
					publicationCreated: microphoneWasPublished,
				});
				this.handleMicrophonePermissionDenied();
			} else {
				logger.error('Failed', e);
				this.transitionMediaState({
					type: 'microphone.enable.failure',
					publicationCreated: microphoneWasPublished,
				});
			}
			throw e;
		}
	}

	async disableMicrophone(room: Room): Promise<void> {
		if (!room.localParticipant) {
			logger.warn('No local participant');
			return;
		}
		this.transitionMediaState({
			type: 'microphone.disable.request',
			hasPublication: this.hasMicrophonePublication(room),
		});
		try {
			const participant = room.localParticipant;
			const microphonePublications = getLocalMicrophonePublications(participant);
			if (microphonePublications.length > 0) {
				const tracks = microphonePublications
					.map((pub) => pub.track)
					.filter((track): track is LocalAudioTrack => Boolean(track));
				await removeVoiceInputProcessor();
				this.stopLocalSpeakingDetector(room);
				await Promise.allSettled(tracks.map((track) => participant.unpublishTrack(track)));
				logger.info('Successfully disabled microphone', {tracksUnpublished: tracks.length});
			}
			this.transitionMediaState({type: 'microphone.disable.success'});
		} catch (e) {
			this.transitionMediaState({type: 'microphone.disable.failure'});
			logger.error('Failed', e);
		}
	}

	async setMicrophoneEnabled(enabled: boolean, room: Room, channelId: string | null): Promise<void> {
		if (enabled) {
			await this.enableMicrophone(room, channelId);
		} else {
			await this.disableMicrophone(room);
		}
	}

	private handleMicrophonePermissionDenied(): void {
		MediaPermission.markMicrophoneExplicitlyDenied();
		ModalCommands.push(
			modal(() => (
				<MicrophonePermissionDeniedModal data-flx="voice.engine.voice-media-manager.microphone-permission-denied-modal" />
			)),
		);
		if (!LocalVoiceState.getSelfMute()) {
			LocalVoiceState.updateSelfMute(true);
		}
	}

	async setCameraEnabled(enabled: boolean, options?: SetCameraEnabledOptions): Promise<void> {
		const room = getRoomFromMediaEngine();
		const {sendUpdate = true, ...restOptions} = options || {};
		if (!room?.localParticipant) {
			logger.warn('No room or local participant');
			return;
		}
		this.transitionMediaState({
			type: 'camera.setEnabled.request',
			enabled,
			currentlyEnabled: room.localParticipant.isCameraEnabled ?? false,
		});
		if (enabled) {
			const nativeResult = await ensureNativePermission('camera');
			if (nativeResult === 'denied') {
				this.transitionMediaState({type: 'camera.failure', actualEnabled: false, permissionDenied: true});
				MediaPermission.markCameraExplicitlyDenied();
				ModalCommands.push(
					modal(() => (
						<CameraPermissionDeniedModal data-flx="voice.engine.voice-media-manager.camera-permission-denied-modal" />
					)),
				);
				return;
			}
			if (nativeResult === 'granted') {
				MediaPermission.updateCameraPermissionGranted();
			}
		}
		try {
			const participant = room.localParticipant;
			await this.enforceCameraPublicationCap(participant, enabled ? 'before camera enable' : 'before camera disable');
			const resolution = VoiceSettings.getCameraResolution();
			const videoResolution = this.getVideoPreset(resolution);
			if (!enabled) {
				VoiceMediaStateCoordinator.applyCameraState(false, {reason: sendUpdate ? 'user' : 'server', sendUpdate});
			}
			await participant.setCameraEnabled(enabled, {resolution: videoResolution, ...restOptions});
			await this.enforceCameraPublicationCap(participant, enabled ? 'after camera enable' : 'after camera disable');
			if (enabled) {
				await this.applyBackgroundToCamera(participant);
				VoiceMediaStateCoordinator.applyCameraState(true, {reason: sendUpdate ? 'user' : 'server', sendUpdate});
			}
			updateLocalParticipantFromRoom(room);
			if (enabled) {
				SoundCommands.playSound(SoundType.CameraOn);
			} else {
				SoundCommands.playSound(SoundType.CameraOff);
			}
			this.transitionMediaState({type: 'camera.success', enabled});
			logger.info('Success', {enabled});
		} catch (e) {
			logger.error('Failed', {enabled, error: e});
			const actual = room.localParticipant?.isCameraEnabled ?? false;
			this.transitionMediaState({type: 'camera.failure', actualEnabled: actual});
			VoiceMediaStateCoordinator.applyCameraState(actual, {reason: sendUpdate ? 'user' : 'server', sendUpdate});
			updateLocalParticipantFromRoom(room);
			if (actual) {
				SoundCommands.playSound(SoundType.CameraOn);
			} else {
				SoundCommands.playSound(SoundType.CameraOff);
			}
		}
	}

	private async enforceCameraPublicationCap(participant: Room['localParticipant'], reason: string): Promise<void> {
		const result = await enforceLocalMediaPublicationCap(participant, 'camera');
		for (const failure of result.failedPublications) {
			logger.warn('Failed to enforce local camera publication cap', {
				error: failure.error,
				reason,
				trackSid: failure.publication.trackSid,
			});
		}
	}

	private async applyBackgroundToCamera(
		participant: Room['localParticipant'],
		options: {warnIfMissing?: boolean} = {},
	): Promise<boolean> {
		const videoPublication = Array.from(participant.videoTrackPublications.values()).find(
			(pub) => pub.source === Track.Source.Camera,
		);
		const track = videoPublication?.track as LocalVideoTrack | undefined;
		if (!track) {
			if (options.warnIfMissing === false) {
				logger.debug('Skipping background refresh: no camera track');
			} else {
				logger.warn('No camera track found to apply background');
			}
			return false;
		}
		await applyBackgroundProcessor(track);
		return true;
	}

	async refreshCameraBackground(room?: Room | null): Promise<void> {
		const refresh = async () => {
			const activeRoom = room === undefined ? getRoomFromMediaEngine() : room;
			const participant = activeRoom?.localParticipant;
			if (!participant) {
				logger.debug('Skipping background refresh: no local participant');
				return;
			}
			const didApply = await this.applyBackgroundToCamera(participant, {warnIfMissing: false});
			if (didApply) {
				updateLocalParticipantFromRoom(activeRoom);
			}
		};
		const pendingRefresh = this.cameraBackgroundRefreshQueue.then(refresh, refresh);
		this.cameraBackgroundRefreshQueue = pendingRefresh.catch(() => {});
		return pendingRefresh;
	}

	private getVideoPreset(resolution: string) {
		switch (resolution) {
			case 'high':
				return VideoPresets.h1080;
			case 'medium':
				return VideoPresets.h720;
			default:
				return VideoPresets.h360;
		}
	}

	async toggleCameraFromKeybind(): Promise<void> {
		const current = LocalVoiceState.getSelfVideo();
		await this.setCameraEnabled(!current, {deviceId: VoiceSettings.getVideoDeviceId()});
	}

	async setScreenShareEnabled(
		enabled: boolean,
		options?: ScreenShareCaptureOptions & {sendUpdate?: boolean; playSound?: boolean; restartIfEnabled?: boolean},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		const room = getRoomFromMediaEngine();
		await VoiceScreenShareManager.setScreenShareEnabled(room, enabled, options, publishOptions);
	}

	prepareScreenShareReconnect(room: Room | null): ScreenShareReconnectSnapshot | null {
		return VoiceScreenShareManager.prepareScreenShareReconnect(room);
	}

	async restoreScreenShareReconnect(
		room: Room | null,
		snapshot: ScreenShareReconnectSnapshot,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceScreenShareManager.restoreScreenShareReconnect(room, snapshot, publishOptions);
	}

	async startDeviceScreenShare(
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		const room = getRoomFromMediaEngine();
		await VoiceScreenShareManager.startDeviceScreenShare(room, options, publishOptions);
	}

	async replaceActiveDisplayScreenShare(
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		const room = getRoomFromMediaEngine();
		return VoiceScreenShareManager.replaceActiveDisplayScreenShare(room, options, publishOptions);
	}

	async replaceActiveDeviceScreenShare(
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		const room = getRoomFromMediaEngine();
		return VoiceScreenShareManager.replaceActiveDeviceScreenShare(room, options, publishOptions);
	}

	async startNativeDisplayScreenShare(
		nativeOptions: NativeScreenShareOptions,
		options?: {sendUpdate?: boolean; playSound?: boolean},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		const room = getRoomFromMediaEngine();
		await VoiceScreenShareManager.startNativeDisplayScreenShare(room, nativeOptions, options, publishOptions);
	}

	async replaceActiveNativeDisplayScreenShare(
		nativeOptions: NativeScreenShareOptions,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		const room = getRoomFromMediaEngine();
		return VoiceScreenShareManager.replaceActiveNativeDisplayScreenShare(room, nativeOptions, options, publishOptions);
	}

	setScreenShareAudioMuted(muted: boolean): void {
		const room = getRoomFromMediaEngine();
		VoiceScreenShareManager.setScreenShareAudioMuted(room, muted);
	}

	async updateActiveScreenShareSettings(
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		const room = getRoomFromMediaEngine();
		return VoiceScreenShareManager.updateActiveScreenShareSettings(room, options, publishOptions);
	}

	async toggleScreenShareFromKeybind(): Promise<void> {
		const room = getRoomFromMediaEngine();
		await VoiceScreenShareManager.toggleScreenShareFromKeybind(room);
	}

	async ensureLinuxScreenShareAudioPublication(
		linuxRule?: NonNullable<NativeAudioStartOptions['linuxRule']>,
		options?: {includeSelfWindowAudio?: boolean; replaceExisting?: boolean},
	): Promise<boolean> {
		const room = getRoomFromMediaEngine();
		return VoiceScreenShareManager.ensureLinuxScreenShareAudioPublication(room, linuxRule, options);
	}

	async playEntranceSound(): Promise<void> {
		await playEntranceSound();
	}

	resetStreamTracking(): void {
		VoiceScreenShareManager.resetStreamTracking();
		void removeVoiceInputProcessor();
		this.stopLocalSpeakingDetector();
		this.transitionMediaState({type: 'media.reset'});
	}

	syncVoiceState(partial: VoiceStateSyncPartial): void {
		syncLocalVoiceStateWithServer(partial);
	}

	applyLocalAudioPreferencesForUser(userId: string, room: Room | null): void {
		applyLocalAudioPreferencesForUserImpl(userId, room);
	}

	applyAllLocalAudioPreferences(room: Room | null): void {
		applyAllLocalAudioPreferencesImpl(room);
	}

	applyLocalInputVolume(room: Room | null): void {
		if (!room?.localParticipant) {
			return;
		}
		const audioTrack = this.getLocalAudioTrack(room);
		updateVoiceInputGain(audioTrack);
	}

	setLocalVideoDisabled(identity: string, disabled: boolean, room: Room | null, connectionId: string | null): void {
		if (!connectionId) {
			logger.warn('No connection ID');
			return;
		}
		CallMediaPrefs.setVideoDisabled(connectionId, identity, disabled);
		if (!room) return;
		const p = room.remoteParticipants.get(identity);
		if (!p) return;
		p.videoTrackPublications.forEach((pub) => {
			if (pub.source !== Track.Source.Camera) {
				logger.debug('Ignoring non-camera publication in local video subscription update', {
					identity,
					source: pub.source,
					trackSid: pub.trackSid,
				});
				return;
			}
			try {
				if (disabled) {
					pub.setSubscribed(false);
					logger.debug('Unsubscribed from track', {
						identity,
						source: pub.source,
						trackSid: pub.trackSid,
					});
					return;
				}
				pub.setSubscribed(true);
				logger.debug('Re-subscribed to local video track', {
					identity,
					trackSid: pub.trackSid,
					trackType: 'camera',
				});
			} catch (err) {
				logger.error('Failed to update subscription', {
					error: err,
					identity,
					source: pub.source,
					disabled,
				});
			}
		});
	}

	applyPushToTalkHold(held: boolean, room: Room | null, getCurrentUserVoiceState: () => VoiceState | null): void {
		applyPushToTalkHoldImpl(held, room, getCurrentUserVoiceState, this.syncVoiceState);
		this.updateMediaAudioControls();
		this.syncLocalSpeakingOverride(room);
	}

	applyPushToMuteHold(held: boolean, room: Room | null, getCurrentUserVoiceState: () => VoiceState | null): void {
		applyPushToMuteHoldImpl(held, room, getCurrentUserVoiceState, this.syncVoiceState);
		this.updateMediaAudioControls();
		this.syncLocalSpeakingOverride(room);
	}

	handlePushToTalkModeChange(room: Room | null, getCurrentUserVoiceState: () => VoiceState | null): void {
		handlePushToTalkModeChangeImpl(room, getCurrentUserVoiceState, this.syncVoiceState);
		this.updateMediaAudioControls();
		this.syncLocalSpeakingOverride(room);
	}

	getMuteReason(voiceState: VoiceState | null, guildId?: string | null, channelId?: string | null): VoiceMuteReason {
		return getMuteReasonImpl(voiceState, guildId, channelId);
	}

	private getLocalAudioTrack(room: Room): LocalAudioTrack | null {
		const micPublication = this.getMicrophonePublication(room);
		return (micPublication?.track as LocalAudioTrack) ?? null;
	}

	private getEffectiveAudioState(override?: {serverMute?: boolean; serverDeaf?: boolean}): EffectiveAudioState {
		return getEffectiveAudioState(override);
	}

	private setAudioPublicationsMuted(room: Room, micMuted: boolean, reason: string): void {
		const muteStreamAudio = VoiceSettings.getMuteStreamAudio();
		for (const publication of room.localParticipant.audioTrackPublications.values()) {
			if (!publication) continue;
			let target: boolean;
			if (publication.source === Track.Source.Microphone) {
				target = micMuted;
			} else if (publication.source === Track.Source.ScreenShareAudio) {
				target = muteStreamAudio;
			} else {
				continue;
			}
			const operation = target ? publication.mute() : publication.unmute();
			operation.catch((error) =>
				logger.error(target ? 'Failed to mute publication' : 'Failed to unmute publication', {
					error,
					reason,
					source: publication.source,
				}),
			);
		}
	}

	private getLocalSpeakingOverrideState(room: Room): boolean | null {
		if (!room.localParticipant) return false;
		const publication = this.getMicrophonePublication(room);
		return resolveLocalSpeakingOverrideState({
			pushToTalkActive: isPushToTalkActive(),
			pushToMuteActive: isPushToMuteActive(),
			pushToMuteHeld: Keybind.pushToMuteHeld,
			selfDeaf: LocalVoiceState.getSelfDeaf(),
			effectiveSelfMute: getEffectiveSelfMuteForVoiceStatePayload(),
			hasMicrophonePublication: Boolean(publication),
			microphonePublicationMuted: publication?.isMuted ?? true,
		});
	}

	private setLocalParticipantSpeaking(room: Room, speaking: boolean): void {
		const localParticipant = room.localParticipant;
		if (!localParticipant || localParticipant.isSpeaking === speaking) {
			return;
		}
		const participantWithSetter = localParticipant as typeof localParticipant & {
			setIsSpeaking?: (speaking: boolean) => void;
		};
		participantWithSetter.setIsSpeaking?.(speaking);
		updateLocalParticipantFromRoom(room);
	}

	private syncLocalSpeakingOverride(room: Room | null): void {
		if (!room?.localParticipant) return;
		const speaking = this.getLocalSpeakingOverrideState(room);
		if (speaking === null) return;
		this.speakingSilenceStartedAt = null;
		this.setLocalParticipantSpeaking(room, speaking);
	}

	private startLocalSpeakingDetector(room: Room, track: MediaStreamTrack | null): void {
		this.stopLocalSpeakingDetector(room);
		if (!track || track.readyState === 'ended' || !room.localParticipant) {
			return;
		}
		const AudioContextCtor =
			window.AudioContext || (window as typeof window & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
		if (!AudioContextCtor) {
			return;
		}
		try {
			const audioContext = new AudioContextCtor({latencyHint: 'interactive'});
			const sourceNode = audioContext.createMediaStreamSource(new MediaStream([track]));
			const analyserNode = audioContext.createAnalyser();
			analyserNode.fftSize = 256;
			analyserNode.smoothingTimeConstant = 0.15;
			sourceNode.connect(analyserNode);
			this.speakingAudioContext = audioContext;
			this.speakingSourceNode = sourceNode;
			this.speakingAnalyserNode = analyserNode;
			this.speakingSilenceStartedAt = null;
			if (audioContext.state === 'suspended') {
				void audioContext.resume().catch(() => {});
			}
			const samples = new Uint8Array(analyserNode.fftSize);
			const releaseDelayMs = SPEAKING_LOCAL_RELEASE_MS;
			const threshold = getLocalSpeakingThresholdRms(VoiceSettings.getVadThreshold());
			const localParticipant = room.localParticipant;
			const setSpeaking = (speaking: boolean): void => {
				this.setLocalParticipantSpeaking(room, speaking);
			};
			const onTrackEnded = (): void => {
				this.transitionMediaState({type: 'microphone.publication.ended'});
				this.stopLocalSpeakingDetector(room);
			};
			track.addEventListener('ended', onTrackEnded, {once: true});
			this.speakingTrackEndedCleanup = () => {
				track.removeEventListener('ended', onTrackEnded);
			};
			const tick = (): void => {
				this.speakingTimerId = null;
				if (this.speakingAnalyserNode !== analyserNode) {
					return;
				}
				if (track.readyState === 'ended') {
					this.stopLocalSpeakingDetector(room);
					return;
				}
				const localSpeakingOverride = this.getLocalSpeakingOverrideState(room);
				if (localSpeakingOverride !== null) {
					this.speakingSilenceStartedAt = null;
					if (localParticipant.isSpeaking !== localSpeakingOverride) {
						setSpeaking(localSpeakingOverride);
					}
					this.speakingTimerId = window.setTimeout(tick, LOCAL_SPEAKING_ANALYSER_INTERVAL_MS);
					return;
				}
				analyserNode.getByteTimeDomainData(samples);
				let sumSquares = 0;
				for (let i = 0; i < samples.length; i++) {
					const normalized = (samples[i]! - 128) / 128;
					sumSquares += normalized * normalized;
				}
				const rms = Math.sqrt(sumSquares / samples.length);
				const now = performance.now();
				const shouldSpeak = rms >= threshold;
				if (shouldSpeak) {
					this.speakingSilenceStartedAt = null;
					if (!localParticipant.isSpeaking) {
						setSpeaking(true);
					}
				} else {
					this.speakingSilenceStartedAt ??= now;
					if (localParticipant.isSpeaking && now - this.speakingSilenceStartedAt >= releaseDelayMs) {
						setSpeaking(false);
					}
				}
				this.speakingTimerId = window.setTimeout(tick, LOCAL_SPEAKING_ANALYSER_INTERVAL_MS);
			};
			this.speakingTimerId = window.setTimeout(tick, LOCAL_SPEAKING_ANALYSER_INTERVAL_MS);
			this.transitionMediaState({type: 'speakingDetector.attach'});
		} catch (error) {
			logger.warn('Failed to start local speaking detector', {error});
			this.stopLocalSpeakingDetector(room);
		}
	}

	private stopLocalSpeakingDetector(room?: Room | null): void {
		this.transitionMediaState({type: 'speakingDetector.detach'});
		if (this.speakingTimerId !== null) {
			window.clearTimeout(this.speakingTimerId);
			this.speakingTimerId = null;
		}
		this.speakingTrackEndedCleanup?.();
		this.speakingTrackEndedCleanup = null;
		try {
			this.speakingSourceNode?.disconnect();
		} catch {}
		try {
			this.speakingAnalyserNode?.disconnect();
		} catch {}
		void this.speakingAudioContext?.close().catch(() => {});
		this.speakingAudioContext = null;
		this.speakingSourceNode = null;
		this.speakingAnalyserNode = null;
		this.speakingSilenceStartedAt = null;
		if (room) {
			this.setLocalParticipantSpeaking(room, false);
		}
	}
}

export default new VoiceMediaManager();
